<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useCaptcha } from '@/hooks/business/captcha';
import { useAntdForm, useFormRules } from '@/hooks/common/form';
import { fetchCheckPwd, fetchModifyPhone, fetchModifyPwd, fetchModifyUserInfo, fetchWithdraw } from '@/service/api';
import { useAuthStore } from '@/store/modules/auth';
import { useBoolean } from '~/packages/hooks/src';
import { fetchGetDashboardData } from '@/service/api/dashboard-api';

defineOptions({
  name: 'ModifyPwdModal'
});

const props = defineProps<{
  operateType: AntDesign.TableOperateType;
}>();

const visible = defineModel<boolean>('visible', {
  default: false
});

const { formRef, validate, resetFields } = useAntdForm();
const { createRequiredRule, createConfirmPwdRule, patternRules, formRules } = useFormRules();
const { bool: isSubmitLoading, setTrue: startSubmitLoading, setFalse: stopSubmitLoading } = useBoolean();
const { bool: incomeLoading, setTrue: startIncomeLoading, setFalse: stopIncomeLoading } = useBoolean();
const { label, isCounting, loading, getCaptcha } = useCaptcha();
const authStore = useAuthStore();

const model = ref<Record<string, any>>({});
const isPwdSet = ref<boolean>(false);
// 用户收入信息
const userIncome = ref<{
  // 余额
  balance: number;
  // 待结算
  pending_settlement: number;
}>({
  balance: 0,
  pending_settlement: 0
});

const rules: any = computed(() => {
  return {
    old_password: [createRequiredRule('旧密码必填'), patternRules?.pwd],
    new_password: [createRequiredRule('新密码必填'), patternRules?.pwd],
    confirm_password: [
      createRequiredRule('确认密码必填'),
      createConfirmPwdRule(model.value.confirm_password),
      patternRules?.pwd
    ],
    new_phone: formRules.phone,
    code: formRules.code,
    name: createRequiredRule('用户名必填'),
    amount: [
      createRequiredRule('提现金额必填'),
      patternRules.amount,
      {
        type: 'number',
        min: 1,
        message: '最小提现金额为1元',
        trigger: 'change'
      },
      {
        type: 'number',
        max: userIncome.value.balance,
        message: '提现金额不能大于当前余额',
        trigger: 'change'
      }
    ]
  };
});

const title = computed(() => {
  const titles: Partial<Record<AntDesign.TableOperateType, string>> = {
    add: '修改个人信息',
    copy: '修改手机号',
    edit: '修改密码',
    batch: '收益提现'
  };
  return titles?.[props.operateType];
});

// 关闭抽屉
const closeModal = () => {
  visible.value = false;
};

// 检查密码是否设置
const checkPwd = async () => {
  startSubmitLoading();
  try {
    const res: any = await fetchCheckPwd();
    if (res.code === 0) {
      isPwdSet.value = res?.data?.has_password;
    } else {
      isPwdSet.value = false;
      window.$message?.error(res.msg || '检查密码失败');
    }
  } catch (error) {
    console.error(error);
  } finally {
    stopSubmitLoading();
  }
};

// 确定
const handleSubmit = async () => {
  await validate();
  startSubmitLoading();
  let req: any = null;
  // 修改密码
  if (props.operateType === 'edit') {
    req = fetchModifyPwd({
      old_password: model.value.old_password,
      new_password: model.value.new_password
    });
  } else if (props.operateType === 'copy') {
    req = fetchModifyPhone({
      new_phone: model.value.new_phone,
      code: model.value.code
    });
  } else if (props.operateType === 'batch') {
    req = fetchWithdraw({
      amount: model.value.amount,
      remark: model.value.remark
    });
  } else {
    req = fetchModifyUserInfo(authStore.userInfo.id, {
      name: model.value.name
    });
  }
  try {
    const res: any = await req;
    if (res.code === 0) {
      window.$message?.success('操作成功');
      closeModal?.();
      // 更新用户信息
      authStore?.initUserInfo();
    } else {
      window.$message?.error(res.msg || '操作失败');
    }
  } catch (error) {
    console.error(error);
  } finally {
    stopSubmitLoading();
  }
};

// 获取用户收入信息
async function getUserIncome() {
  try {
    startIncomeLoading();
    const res: any = await fetchGetDashboardData();
    if (res.code === 0) {
      const incomeInfo = res?.data || {};
      userIncome.value = {
        balance: incomeInfo?.balance || 0,
        pending_settlement: incomeInfo?.pending_settlement || 0
      };
    }
  } catch (error) {
    console.error(error);
  } finally {
    stopIncomeLoading();
  }
}

function handleAllWithdraw() {
  model.value.amount = userIncome.value.balance;
}

watch(visible, () => {
  if (visible.value) {
    resetFields();
    if (props.operateType === 'edit') {
      checkPwd();
    } else if (props.operateType === 'copy') {
      model.value.new_phone = authStore.userInfo.phone;
    } else if (props.operateType === 'add') {
      model.value.name = authStore.userInfo.name;
    } else {
      getUserIncome();
    }
  }
});
</script>

<template>
  <ADrawer v-model:open="visible" :title="title" :width="360">
    <!-- 提现的时候显示余额和待结算信息 -->
    <template v-if="operateType === 'batch'">
      <ASpin :spinning="incomeLoading">
        <div class="mb-3 flex-y-center justify-around">
          <div>
            <AStatistic title="当前余额" :value="userIncome.balance" prefix="￥" />
          </div>
          <div>
            <AStatistic title="待结算" :value="userIncome.pending_settlement" prefix="￥" />
          </div>
        </div>
      </ASpin>
    </template>
    <ASpin :spinning="isSubmitLoading">
      <AForm ref="formRef" layout="vertical" :model="model" :rules="rules">
        <!-- 修改密码 -->
        <template v-if="operateType === 'edit'">
          <AFormItem label="旧密码" name="old_password">
            <AInput v-model:value="model.old_password" placeholder="请输入旧密码" type="password" />
          </AFormItem>
          <AFormItem label="新密码" name="new_password">
            <AInput v-model:value="model.new_password" placeholder="请输入新密码" type="password" />
          </AFormItem>
          <AFormItem label="确认密码" name="confirm_password">
            <AInput v-model:value="model.confirm_password" placeholder="请输入确认密码" type="password" />
          </AFormItem>
        </template>
        <!-- 修改手机号 -->
        <template v-if="operateType === 'copy'">
          <AFormItem name="new_phone" label="新手机号">
            <AInput
              v-model:value="model.new_phone"
              :placeholder="$t('page.login.common.phonePlaceholder')"
              allow-clear
            />
          </AFormItem>
          <AFormItem name="code" label="验证码">
            <div class="w-full flex-y-center gap-16px">
              <AInput v-model:value="model.code" :placeholder="$t('page.login.common.codePlaceholder')" allow-clear />
              <AButton :disabled="isCounting" :loading="loading" @click="getCaptcha(model.new_phone)">
                {{ label }}
              </AButton>
            </div>
          </AFormItem>
        </template>
        <!-- 修改个人信息 -->
        <template v-if="operateType === 'add'">
          <AFormItem name="name" label="用户名">
            <AInput v-model:value="model.name" placeholder="请输入用户名" allow-clear />
          </AFormItem>
        </template>
        <!-- 收益提现 -->
        <template v-if="operateType === 'batch'">
          <AFormItem name="amount" label="提现金额" validate-first>
            <template #extra>
              <div class="flex-y-center justify-between">
                <ATypographyText type="secondary" class="text-xs">提现金额最小值为1元</ATypographyText>
                <ATypographyLink class="text-xs" @click="handleAllWithdraw">全部提现</ATypographyLink>
              </div>
            </template>
            <AInputNumber
              v-model:value="model.amount"
              placeholder="请输入提现金额"
              allow-clear
              class="w-full"
              prefix="￥"
            />
          </AFormItem>
          <AFormItem name="remark" label="提现备注">
            <ATextarea
              v-model:value="model.remark"
              placeholder="请输入提现备注"
              allow-clear
              :rows="4"
              :maxlength="100"
              show-count
            />
          </AFormItem>
        </template>
      </AForm>
    </ASpin>
    <template #footer>
      <ASpace :size="16">
        <AButton @click="closeModal">取消</AButton>
        <AButton type="primary" :loading="isSubmitLoading" @click="handleSubmit">确定</AButton>
      </ASpace>
    </template>
  </ADrawer>
</template>

<style scoped></style>
